import { useQuery } from '@apollo/client';
import styled from '@emotion/styled';
import { Link } from '@reach/router';
import LockIcon from '@material-ui/icons/Lock';

import colors from 'lib/colors';
import { gamePreviewIdQuery } from 'pages/Lobby/operations';
import { PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  GameDifficulty,
  gamePreviewId,
  gamePreviewIdVariables,
  GamePreviewType,
} from '../../../graphql/types';
import { useMutationErrorHandler } from '../../../utils/mutation-utils';
import FloatingProfilePosition from '../Profile/FloatingProfilePosition';
import useSetGameDifficulty from '../hooks/useSetGameDifficulty';
import { Tooltip } from '@material-ui/core';

interface Props {
  state: GameDifficulty;
  skipPreview: boolean;
  isMediumGameActive?: boolean;
  opponentCount?: number;
}

const LobbyDifficulty = ({
  state,
  skipPreview,
  isMediumGameActive,
  opponentCount,
}: Props) => {
  const [t] = useTranslation();
  const handleError = useMutationErrorHandler();
  const [gameDifficulty, setDifficulty] = useState(state);

  const [setGameDifficulty] = useSetGameDifficulty({
    reload: true,
    onCompleted: (difficulty) => {
      setDifficulty(difficulty);
    },
    onError: handleError,
  });

  const handleChangeDifficultyButtonClick = useCallback(
    async (newDifficulty: GameDifficulty) => {
      if (gameDifficulty !== newDifficulty) {
        if (newDifficulty === GameDifficulty.MEDIUM && !isMediumGameActive) {
          return;
        }
        await setGameDifficulty({
          variables: { input: { gameDifficulty: newDifficulty } },
        });
      }
    },
    [gameDifficulty, isMediumGameActive, setGameDifficulty],
  );

  useEffect(() => {
    if (!isMediumGameActive && gameDifficulty === GameDifficulty.MEDIUM) {
      (async () =>
        await handleChangeDifficultyButtonClick(GameDifficulty.EASY))();
    }
  }, [isMediumGameActive, gameDifficulty, handleChangeDifficultyButtonClick]);

  const { data } = useQuery<gamePreviewId, gamePreviewIdVariables>(
    gamePreviewIdQuery,
    {
      fetchPolicy: 'cache-first',
      variables: {
        input: { gamePreviewType: GamePreviewType.MEDIUM_GAME_INTRO },
      },
    },
  );

  return (
    <>
      <Nav>
        <Link to="/" css={{ textDecoration: 'none', position: 'relative' }}>
          <NavigationLinkContent
            isActive={gameDifficulty === GameDifficulty.EASY}
            onClick={() =>
              handleChangeDifficultyButtonClick(GameDifficulty.EASY)
            }
          >
            {t('lobby.difficulty.easy', 'Základní hra')}
          </NavigationLinkContent>
        </Link>
        <Link
          to={
            skipPreview || !isMediumGameActive
              ? ''
              : `/game/${data?.gamePreviewId}`
          }
          css={{ textDecoration: 'none', position: 'relative' }}
        >
          <NavigationLinkContent
            isActive={gameDifficulty === GameDifficulty.MEDIUM}
            onClick={() =>
              isMediumGameActive
                ? handleChangeDifficultyButtonClick(GameDifficulty.MEDIUM)
                : null
            }
            disabled={!isMediumGameActive}
          >
            <MediumGameLink isMediumGameActive={isMediumGameActive}>
              {t('lobby.difficulty.medium', 'Pokročilá hra')}
            </MediumGameLink>

            {!!opponentCount && (
              <OpponentCountBadge>{opponentCount}</OpponentCountBadge>
            )}
          </NavigationLinkContent>
        </Link>
      </Nav>
      {gameDifficulty === GameDifficulty.MEDIUM && <FloatingProfilePosition />}
    </>
  );
};

export default LobbyDifficulty;

const Nav = styled('nav')({
  marginTop: '.5em',
  display: 'flex',
  alignSelf: 'center',
  position: 'relative',
  marginBottom: '1.5em',
  '@media screen and (max-width: 1000px)': {
    marginTop: 8,
  },
  '&::before': {
    content: '" "',
    width: '100%',
    height: '3px',
    backgroundColor: '#252525',
    position: 'absolute',
    borderRadius: '3px',
    bottom: '-3px',
    left: 0,
  },
});

interface NavigationLinkContentProps {
  isActive?: boolean;
  disabled?: boolean;
}

export const NavigationLinkContent = styled('div')<NavigationLinkContentProps>(
  ({ isActive, disabled }) => ({
    fontStyle: 'normal',
    fontWeight: 'bold',
    fontSize: '1.2em',
    height: ' 100%',
    background: 'none',
    padding: '0.6em 1em',
    whiteSpace: 'nowrap',
    // color: linkActive ? colors.white : colors.lobbyText,
    color: colors.white,
    borderRadius: 0,
    backgroundColor: 'transparent',
    textDecoration: 'none',
    position: 'relative',
    ':focus': {
      boxShadow: 'none',
    },
    ':hover': {
      color: colors.white,
    },
    ':hover::after': {
      backgroundColor: !disabled ? colors.lobbyOrange : undefined,
    },
    '&::after': {
      content: '" "',
      width: '100%',
      height: '3px',
      backgroundColor: isActive ? colors.lobbyOrange : 'transparent',
      position: 'absolute',
      borderRadius: '3px',
      bottom: '-3px',
      left: 0,
    },
    opacity: disabled ? 0.5 : undefined,
  }),
);

const MediumGameLinkWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const LinkTooltip = (props: PropsWithChildren<any>) => {
  const [t] = useTranslation();
  return (
    <Tooltip
      title={
        <>
          {t(
            'lobby.forAccessContactAdmin',
            'Pro přístup kontaktujte kontaktujte svého administrátora',
          )}
        </>
      }
    >
      {props.children}
    </Tooltip>
  );
};

const MediumGameLink = (props: PropsWithChildren<any>) => {
  const Wrapper = props.isMediumGameActive ? styled.div`` : LinkTooltip;

  return (
    <Wrapper>
      <MediumGameLinkWrapper>
        {!props.isMediumGameActive && <LockIcon />}
        {props.children}
      </MediumGameLinkWrapper>
    </Wrapper>
  );
};

const OpponentCountBadge = styled('div')({
  position: 'absolute',
  top: -4,
  right: -4,
  background: colors.lobbyOrange,
  color: colors.white,
  borderRadius: '50%',
  minWidth: 25,
  padding: 2,
  fontSize: 'initial',
  fontWeight: 'normal',
});
